import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Container, Typography } from '@mui/material';
import { showError, toast } from '../../../utils/errorHandler';
import { RegistrationForm } from '../../../components/forms/RegistrationForm';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';
import { asaasService } from '../../../services/asaasService';
import { apiClientBff } from '../../../services/apiBff';
import { DEFAULT_PAID_PLAN, normalizePaidPlanType, onboardingService } from '../../onboarding/services/onboardingService';

export const OnboardingWebViewSignupPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const plan = normalizePaidPlanType(params.get('plan')) || DEFAULT_PAID_PLAN;

  const [isLoading, setIsLoading] = useState(false);

  const handleSuccess = async (message: string, email?: string, meta?: { token?: string; password?: string }) => {
    toast.success(message);
    const normalizedEmail = email ? email.trim().toLowerCase() : '';
    const emailParam = normalizedEmail ? `&email=${encodeURIComponent(normalizedEmail)}` : '';

    if (normalizedEmail) {
      try {
        const access: any = await asaasService.checkAccessByEmail(normalizedEmail, { token: meta?.token });
        const hasAccess = Boolean(access?.accessGranted);
        if (hasAccess) {
          await apiClientBff.authPost('/auth/forgot-password', { email: normalizedEmail });
          setTimeout(() => {
            navigate(`/auth/finish-registration?email=${encodeURIComponent(normalizedEmail)}&sent=true`, { replace: true });
          }, 1200);
          return;
        }
      } catch (err) {
        console.error('Error checking Asaas access post-registration (webview):', err);
        toast.error('Erro ao verificar assinatura. Tente novamente.');
        return;
      }
    }

    const nextPath = `/onboarding-webview/plan-selection?plan=${encodeURIComponent(plan)}${emailParam}`;
    onboardingService.storeOnboardingTrialBootstrapState({
      email: normalizedEmail,
      shouldCreate: true,
      token: meta?.token,
    });
    setTimeout(() => {
      navigate(nextPath, {
        replace: true,
        state: {
          registrationJustCreated: true,
          registrationToken: meta?.token,
          registrationEmail: normalizedEmail,
          registrationPassword: meta?.password,
        },
      });
    }, 1200);
  };

  const handleError = (error: string) => {
    toast.error(error);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: DESIGN_TOKENS.colors.background.default,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 4,
      }}
    >
      <Container maxWidth="sm" sx={{ p: 0 }}>
        <Box
          sx={{
            bgcolor: '#fff',
            border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
            borderRadius: 4,
            boxShadow: '0 24px 48px rgba(0,0,0,0.08)',
            p: { xs: 3, sm: 4 },
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Box component="img" src="/logo.svg" alt="MUU" sx={{ height: 28, mb: 1 }} />
            <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
              Cadastro rápido para continuar no app
            </Typography>
          </Box>

          <RegistrationForm
            isLoading={isLoading}
            onLoadingChange={setIsLoading}
            onSuccess={handleSuccess}
            onError={handleError}
            hideLoginLink
            variant="webview"
          />
        </Box>
      </Container>
    </Box>
  );
};

export default OnboardingWebViewSignupPage;
